import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import FilterBox from '../../components/filter-box/FilterBox';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/account/AccountContexts';
import { useFilters } from '../../contexts/filters/FiltersContext';
import ModelSelect, { useModelSelectOptions } from '../model-select/ModelSelect';
import { getPeriodParam } from '../../helpers/filters/FiltersHelper';
import {
  calculateStandardRange,
  standardPeriodOptions,
} from '../../helpers/filters/DateRangeHelper';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { useTranslation } from 'react-i18next';
import { FilterItem } from '../../components/filter-box/models/FilterBoxModel';
import { MonitorPeriod } from '../../types/monitor/MonitorPeriod';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';
import { ChatType } from '../../types/monitor/ChatType';
import './FiltersPanel.scss';

const buildUrlWithModels = (
  basePath: string,
  period: string,
  selectedTypes: ChatType[],
  allAvailable: ChatType[],
  currentSearch: string
): string => {
  const parts = basePath.split('/').filter(Boolean);
  const newParts = parts.slice(0, -2);
  const path = `/${newParts.join('/')}/${period}/all`;

  const params = new URLSearchParams(currentSearch);
  if (selectedTypes.length >= allAvailable.length) {
    params.delete('models');
  } else {
    params.set('models', selectedTypes.map((t) => t.toLowerCase()).join(','));
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

interface FilterProps {
  baseUrl: string;
  showDateRange?: boolean;
  showChatTypeDropdown?: boolean;
  showPeriodTabs?: boolean;
  children?: ReactNode;
}

const FilterPanel: React.FC<FilterProps> = ({
  baseUrl,
  showDateRange = true,
  showChatTypeDropdown = true,
  showPeriodTabs = true,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const {
    monitorPeriod,
    selectedChatType,
    selectedChatTypes,
    range,
    setRange,
    dateRange,
    selectedPeriod: selectedPeriodContext,
    setSelectedPeriod: setSelectedPeriodContext,
  } = useFilters();
  const [selectedPeriod, setSelectedPeriod] = useState<StandardPeriodType>(
    selectedPeriodContext || 'LAST_30_DAYS'
  );

  useEffect(() => {
    if (selectedPeriodContext) {
      setSelectedPeriod(selectedPeriodContext);
    }
  }, [selectedPeriodContext]);

  const { availableTypes } = useModelSelectOptions();

  const periodOptions = useMemo(() => standardPeriodOptions(t), [t]);

  const handleDateRangeChange = (newRange: Date[]): void => {
    setRange(newRange);
  };

  // Wybór modeli żyje w URL-u (`?models=`), nie w stanie komponentu — stąd nawigacja zamiast setState.
  const handleModelsChange = (types: ChatType[]) => {
    if (!monitorPeriod) return;

    navigate(
      buildUrlWithModels(baseUrl, getPeriodParam(monitorPeriod), types, availableTypes, location.search)
    );
  };

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) {
      return [];
    }

    const periods: { period: string; label: string; monitorPeriod: MonitorPeriod }[] = [
      { period: 'daily', label: t('dailyFilter'), monitorPeriod: 'DAILY' },
      { period: 'weekly', label: t('weeklyFilter'), monitorPeriod: 'WEEKLY' },
      { period: 'monthly', label: t('monthlyFilter'), monitorPeriod: 'MONTHLY' },
    ];

    return periods.map(({ period, label, monitorPeriod: periodType }) => ({
      url: buildUrlWithModels(baseUrl, period, selectedChatTypes, availableTypes, location.search),
      label,
      isActive: monitorPeriod === periodType,
    }));
  }, [baseUrl, selectedChatType, selectedChatTypes, monitorPeriod, availableTypes, location.search, t]);

  if (!accountResponse || !monitorPeriod || !selectedChatType || !range) {
    return <></>;
  }

  return (
    <div className="filters-panel">
      {children}
      {showChatTypeDropdown && (
        <ModelSelect selected={selectedChatTypes} onChange={handleModelsChange} />
      )}
      {showDateRange && (
        <FilterBox
          items={showPeriodTabs ? periodFilterItems : []}
          children={
            <DateRangePicker
              value={range}
              onChange={handleDateRangeChange}
              selectedPeriod={selectedPeriod}
              onPeriodChange={(period) => {
                const periodValue = period as StandardPeriodType;
                setSelectedPeriod(periodValue);
                if (setSelectedPeriodContext) {
                  setSelectedPeriodContext(periodValue);
                }
              }}
              minDate={dateRange?.oldestDate}
              maxDate={dateRange?.newestDate}
              periodOptions={periodOptions}
              calculateRange={calculateStandardRange}
              defaultPeriod={selectedPeriodContext || 'LAST_30_DAYS'}
              oldestDate={dateRange?.oldestDate}
            />
          }
        />
      )}
    </div>
  );
};

export default FilterPanel;
